import { Color, Palette, useTheme } from "@mui/material";
import { useCallback, useMemo } from "react";
import { BASE_SVG_PATH, ICON_TEMPLATES } from "../constants/marker-styles";
import { MapMarker } from "@/components/map/map.types";

// TODO: Refactor further if possible
// Global icon cache for maximum performance
const iconCache = new Map<string, google.maps.Icon>();

// Comprehensive marker type constants
const MARKER_TYPES = {
  // Status types
  COMPLETED: "completed",
  PARTIALLY_COMPLETED: "partiallyCompleted",
  CANT_PLOW: "cantPlow",
  IN_PROGRESS: "inProgress",
  SKIPPED: "skipped",
  DELAYED: "delayed",
  TO_BE_PLOWED: "toBePlowed",

  // Special types
  UNASSIGNED: "unassigned",
} as const;

// Group related marker types
const MARKER_TYPE_GROUPS = {
  COMPLETED_STATES: [
    MARKER_TYPES.COMPLETED,
    MARKER_TYPES.PARTIALLY_COMPLETED,
    MARKER_TYPES.CANT_PLOW,
    MARKER_TYPES.IN_PROGRESS,
  ],
  STATUS_TYPES: [
    MARKER_TYPES.SKIPPED,
    MARKER_TYPES.CANT_PLOW,
    MARKER_TYPES.PARTIALLY_COMPLETED,
    MARKER_TYPES.COMPLETED,
    MARKER_TYPES.IN_PROGRESS,
  ],
} as const;

// Consolidated color constants
const COLORS = {
  SELECTED_COMPLETED: "#2E3133",
  UNSELECTED_COMPLETED: "#5D5E61",
  SELECTED_SKIPPED: "#454749",
  UNSELECTED_SKIPPED: "#AAABAE",
  SELECTED_UNASSIGNED: "#454749",
  UNSELECTED_UNASSIGNED: "#AAABAE",
  SELECTED_STROKE_DARK: "#1A1C1E",
  UNSELECTED_STROKE_MEDIUM: "#5D5E61",
  UNSELECTED_STROKE_LIGHT: "#454749",
  TRANSPARENT: "",
} as const;

// Helper function to darken hex colors
const darkenHexColor = (hex: string, factor: number): string => {
  // Remove the hash if present
  const colorHex = hex.replace("#", "");

  // Convert to RGB
  const num = parseInt(colorHex, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;

  // Darken each component
  const newR = Math.max(0, Math.floor(r * (1 - factor)));
  const newG = Math.max(0, Math.floor(g * (1 - factor)));
  const newB = Math.max(0, Math.floor(b * (1 - factor)));

  // Convert back to hex
  return `#${((newR << 16) | (newG << 8) | newB).toString(16).padStart(6, "0")}`;
};

// Plowed pins keep the route color but faded, so dispatchers can tell at a
// glance which route plowed a house. The fade is baked into an opaque color
// (route color blended into the old completed gray) rather than using
// fill-opacity, so the white checkmark stays readable on light map tiles and
// overlapping pins don't muddy each other.
const PLOWED_TYPES: readonly string[] = [
  MARKER_TYPES.COMPLETED,
  MARKER_TYPES.PARTIALLY_COMPLETED,
];
const PLOWED_ROUTE_COLOR_WEIGHT = 0.55;
const SELECTED_DARKEN_FACTOR = 0.2;

const toHex6 = (hex: string): string => {
  const colorHex = hex.replace("#", "");
  return colorHex.length === 3
    ? colorHex
        .split("")
        .map((char) => char + char)
        .join("")
    : colorHex;
};

// Blend `hex` into `baseHex`; weight is how much of `hex` is kept (0..1)
const fadeHexColor = (hex: string, baseHex: string, weight: number): string => {
  const color = parseInt(toHex6(hex), 16);
  const base = parseInt(toHex6(baseHex), 16);

  const mix = (shift: number) =>
    Math.round(
      ((color >> shift) & 255) * weight +
        ((base >> shift) & 255) * (1 - weight),
    );

  return `#${((mix(16) << 16) | (mix(8) << 8) | mix(0)).toString(16).padStart(6, "0")}`;
};

// Comprehensive style configuration lookup table
const MARKER_STYLE_CONFIG = {
  [MARKER_TYPES.SKIPPED]: {
    background: {
      selected: COLORS.SELECTED_SKIPPED,
      unselected: COLORS.UNSELECTED_SKIPPED,
    },
    stroke: {
      selected: COLORS.TRANSPARENT,
      unselected: COLORS.UNSELECTED_STROKE_MEDIUM,
    },
  },
  // Completed states share the same styling
  ...Object.fromEntries(
    MARKER_TYPE_GROUPS.COMPLETED_STATES.map((type) => [
      type,
      {
        background: {
          selected: COLORS.SELECTED_COMPLETED,
          unselected: COLORS.UNSELECTED_COMPLETED,
        },
        stroke: {
          selected: COLORS.SELECTED_STROKE_DARK,
          unselected: COLORS.UNSELECTED_STROKE_LIGHT,
        },
      },
    ]),
  ),
} as const;

// Palette color configuration
const PALETTE_COLOR_CONFIG = {
  [MARKER_TYPES.DELAYED]: {
    background: { shade: 300 },
    stroke: { shade: 500 },
  },
  default: {
    background: { shade: 400, selectedShade: 600, fallbackShade: 500 },
    stroke: { shade: 700 },
  },
} as const;

// Unassigned color configuration
const UNASSIGNED_CONFIG = {
  background: {
    selected: COLORS.SELECTED_UNASSIGNED,
    unselected: COLORS.UNSELECTED_UNASSIGNED,
  },
  stroke: {
    selected: COLORS.SELECTED_STROKE_DARK,
    unselected: COLORS.UNSELECTED_STROKE_MEDIUM,
  },
} as const;

export const useCustomMarkerStyles = (locationMarker: MapMarker) => {
  const theme = useTheme();
  const { palette } = theme;

  // Helper function to check if color is a valid hex code
  const isHexColor = useCallback((color: string): boolean => {
    const hexCodeRegex = /#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})/;
    return hexCodeRegex.test(color);
  }, []);

  // Helper function to get palette color
  const getPaletteColor = useCallback(
    (color: string) => {
      return palette[color as keyof Palette] as Color | undefined;
    },
    [palette],
  );

  // Get style from configuration lookup table
  const getStyleFromConfig = useCallback((type: string, selected: boolean) => {
    const config =
      MARKER_STYLE_CONFIG[type as keyof typeof MARKER_STYLE_CONFIG];
    if (!config) return null;

    return {
      background: selected
        ? config.background.selected
        : config.background.unselected,
      stroke: selected ? config.stroke.selected : config.stroke.unselected,
    };
  }, []);

  // Handle palette-based colors
  const getPaletteBasedStyle = useCallback(
    (
      color: string,
      type: string,
      selected: boolean,
      styleType: "background" | "stroke",
    ): string => {
      const paletteColor = getPaletteColor(color);
      if (!paletteColor) return "";

      const config =
        PALETTE_COLOR_CONFIG[type as keyof typeof PALETTE_COLOR_CONFIG] ||
        PALETTE_COLOR_CONFIG.default;

      if (styleType === "background") {
        if (type === MARKER_TYPES.DELAYED) {
          return paletteColor[config.background.shade as keyof Color] || "";
        }

        const defaultConfig =
          config === PALETTE_COLOR_CONFIG.default
            ? config
            : PALETTE_COLOR_CONFIG.default;

        if (
          selected &&
          paletteColor[defaultConfig.background.fallbackShade as keyof Color]
        ) {
          return (
            paletteColor[
              defaultConfig.background.selectedShade as keyof Color
            ] || ""
          );
        }

        return paletteColor[config.background.shade as keyof Color] || "";
      } else {
        return paletteColor[config.stroke.shade as keyof Color] || "";
      }
    },
    [getPaletteColor],
  );

  // Unified color resolution function
  const resolveColor = useCallback(
    (
      color: string,
      type: string,
      selected: boolean,
      styleType: "background" | "stroke",
    ): string => {
      // 1. Handle unassigned colors
      if (color === MARKER_TYPES.UNASSIGNED) {
        const config = UNASSIGNED_CONFIG[styleType];
        return selected ? config.selected : config.unselected;
      }

      // 2. Plowed pins use a faded route color
      if (PLOWED_TYPES.includes(type)) {
        const routeHex = isHexColor(color)
          ? color
          : (getPaletteColor(color)?.[
              PALETTE_COLOR_CONFIG.default.background.shade
            ] ?? "");

        if (isHexColor(routeHex)) {
          if (styleType === "stroke") {
            return selected ? COLORS.SELECTED_STROKE_DARK : routeHex;
          }

          const faded = fadeHexColor(
            routeHex,
            COLORS.UNSELECTED_COMPLETED,
            PLOWED_ROUTE_COLOR_WEIGHT,
          );
          return selected
            ? darkenHexColor(faded, SELECTED_DARKEN_FACTOR)
            : faded.toUpperCase();
        }
      }

      // 3. Handle configured marker types
      const configStyle = getStyleFromConfig(type, selected);
      if (configStyle) {
        return configStyle[styleType];
      }

      // 4. Handle hex colors (only for background)
      if (styleType === "background" && isHexColor(color)) {
        // Darken hex color when selected, same as in the table
        if (selected) {
          return darkenHexColor(color, 0.2); // Darken by 20%
        }
        return color.toUpperCase();
      }

      // 5. Handle palette colors
      return getPaletteBasedStyle(color, type, selected, styleType);
    },
    [getStyleFromConfig, isHexColor, getPaletteColor, getPaletteBasedStyle],
  );

  // Simplified marker background color function
  const getMarkerBg = useCallback(
    (color: string, type: string, selected: boolean): string => {
      return resolveColor(color, type, selected, "background");
    },
    [resolveColor],
  );

  // Simplified marker stroke color function
  const getStroke = useCallback(
    (color: string, type: string, selected: boolean): string => {
      return resolveColor(color, type, selected, "stroke");
    },
    [resolveColor],
  );

  // Icon generation with no hardcoded strings
  const getMarkerIcon = useCallback(
    (type: string, sequence?: number | null, markerColor?: string): string => {
      // Show sequence number for non-status types (prioritize over unassigned state)
      // Treat -1 as unassigned (don't show sequence number)
      if (
        sequence &&
        sequence > 0 &&
        !(MARKER_TYPE_GROUPS.STATUS_TYPES as readonly string[]).includes(type)
      ) {
        return `<text x="0" y="1" text-anchor="middle" dominant-baseline="central" fill="#fff" font-size="8" font-weight="500" font-family="Arial">${sequence}</text>`;
      }

      // Handle unassigned addresses (only if no sequence number or sequence is -1)
      if (markerColor === MARKER_TYPES.UNASSIGNED || sequence === -1) {
        return ICON_TEMPLATES.unassigned;
      }

      return ICON_TEMPLATES[type as keyof typeof ICON_TEMPLATES] || "";
    },
    [],
  );

  // Generate custom marker icon with global caching for maximum performance
  const customIcon = useMemo(() => {
    const markerType =
      (locationMarker.driverStatus !== MARKER_TYPES.TO_BE_PLOWED
        ? locationMarker.driverStatus
        : locationMarker.order?.status) || "";
    const markerColor =
      locationMarker.region?.color?.toLocaleLowerCase() ||
      locationMarker.color?.toLocaleLowerCase() ||
      MARKER_TYPES.UNASSIGNED;
    const selected = locationMarker.selected || false;
    const sequence = locationMarker.locationSequence;

    // Create cache key for this exact combination
    const cacheKey = `${markerType}-${markerColor}-${selected}-${sequence || "none"}`;

    // Check global cache first
    if (iconCache.has(cacheKey)) {
      return iconCache.get(cacheKey)!;
    }

    const bgColor = getMarkerBg(markerColor, markerType, selected);
    const strokeColor = getStroke(markerColor, markerType, selected);
    const iconContent = getMarkerIcon(markerType, sequence, markerColor);

    // Ultra-compact SVG with pre-computed path
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="19" viewBox="0 0 16 19"><path d="${BASE_SVG_PATH}" fill="${bgColor}" stroke="${strokeColor}"/><g transform="translate(8,8)">${iconContent}</g></svg>`;

    const icon = {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(16, 19),
      anchor: new google.maps.Point(8, 19),
    };

    // Cache the result for future use
    iconCache.set(cacheKey, icon);

    return icon;
  }, [
    `${locationMarker.driverStatus}-${locationMarker.order?.status}-${locationMarker.region?.color}-${locationMarker.color}-${locationMarker.selected}-${locationMarker.locationSequence}`,
  ]);

  return {
    customIcon,
  };
};
